import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import RiskMapChart from './sections/risk-map-chart';
import { EvaluationModel } from './sections/risk-map.utils';
import RiskTrendsChart from './sections/risk-trends-chart';
import styles from './styles.module.scss';
import FeActionsBar from '@components-clean/core/filter-ui/action-bar/fe-action-bar';
import {
    FILTER_DATA,
    FILTERS,
    FilterUIStateUpdate,
    IEventBusMessage,
    IFilterEventBusMessage,
} from '@components-clean/core/filter-ui/action-bar/shared/interfaces';
import NoRecordsMessage from '@components-clean/core/grid/no-records-message';
import Loading from '@components-clean/core/loading/loading';
import Toggle from '@components-clean/core/toggle';
import { RiskDashboardContext, IRiskDashboardContext } from '@pages-clean/risk-assessment/risk-dashboard/context';
import RiskCategoriesChart from '@pages-clean/risk-assessment/risk-dashboard/sections/risk-categories-chart';
import { filterColumns } from '@pages-clean/risk-assessment/risk-dashboard/sections/risk-dashboard-filter-definition';
import { buildFilterableRisks } from '@pages-clean/risk-assessment/risk-dashboard/sections/risk-dashboard-filter.helper';
import RiskDashboardReviewStatus from '@pages-clean/risk-assessment/risk-dashboard/sections/risk-dashboard-review-status';
import { busDispatch, useEventBus } from '@utils-clean/eventBus';
import { NamedItemType, RiskStatusType } from 'types/graphql-global-types';
import { NullableStudy } from '@contexts/local-state-context';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { getIsActiveField } from '@utils/convertor';
import { mapRiskStatus } from '@utils/risk-helper';

import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import {
    getCompleteRisksRiskLog,
    getCompleteRisksRiskLogVariables,
    riskDashboardConfig,
    riskDashboardConfigVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_COMPLETE_RISKS_RISK_LOG, RISK_DASHBOARD_CONFIG } from '@queries/risk-assessment';
import { LoggedInPageProps } from '@config/routes/interfaces';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

interface IRiskDashboardWrapperProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: IRiskDashboardWrapperProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskDashboard' });
    const { t: tRiskLog } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const user = useAuthorization();

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);
    const HasCMControlsPermission =
        StudyHasCM2 &&
        user?.can?.({
            module: Constants.Modules.RAM,
            permission: Permissions.Enabled,
            element: Constants.PageElements.CMControls,
        });

    const { data: latestOOTValuesForPanel, isLoading: ootLoading } = ControlsHubApi.useGetLatestOOTValuesForPanel(
        study?.id,
        HasCMControlsPermission,
    );

    const [includeObsolete, setIncludeObsolete] = useState(false);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] });
    const [trackedChanges, setTrackedChanges] = useState<FilterUIStateUpdate[]>([]);
    const [filterVersion, setFilterVersion] = useState(0);

    useEventBus<IFilterEventBusMessage>(FILTERS, (filters) => {
        setFilterState({ logic: 'and', filters });
    });

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setFilterVersion((v) => v + 1);
    }, [filterState, trackedChanges]);

    const { loading, data } = useQuery<getCompleteRisksRiskLog, getCompleteRisksRiskLogVariables>(
        GET_COMPLETE_RISKS_RISK_LOG,
        {
            variables: { studyId: study ? study.id : null },
            skip: !study?.id,
        },
    );

    useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_QUALITY_ISSUE },
    });

    const { data: panelConfig } = useQuery<riskDashboardConfig, riskDashboardConfigVariables>(RISK_DASHBOARD_CONFIG);

    const panels = panelConfig?.riskDashboardConfig?.panels ?? [];

    const panelComponents: Record<string, React.ReactNode> = {
        RISK_MAP: <RiskMapChart />,
        STUDY_RISK_TRENDS: <RiskTrendsChart />,
        RISK_CATEGORIES: <RiskCategoriesChart />,
        RISK_REVIEW_STATUS: <RiskDashboardReviewStatus />,
    };

    const risks = useMemo(() => data?.risks?.filter(notEmpty) ?? [], [data]);

    const visibleRisks = useMemo(
        () =>
            includeObsolete
                ? risks
                : risks.filter((r) => mapRiskStatus(r.status) !== mapRiskStatus(RiskStatusType.OBSOLETE)),
        [risks, includeObsolete],
    );

    const categories = useMemo(() => filterColumns(HasCMControlsPermission ?? false), [HasCMControlsPermission]);

    const filterableRisks = useMemo(
        () => buildFilterableRisks(visibleRisks, tRiskLog, HasCMControlsPermission ?? false, latestOOTValuesForPanel),
        [visibleRisks, tRiskLog, HasCMControlsPermission, latestOOTValuesForPanel],
    );

    useEffect(() => {
        if (HasCMControlsPermission && ootLoading) return;
        busDispatch<IEventBusMessage>(FILTER_DATA, { categories, data: filterableRisks, clearFilter: false });
    }, [categories, filterableRisks, HasCMControlsPermission, ootLoading]);

    const filteredRisks = useMemo(() => {
        const passingIds = new Set(filterBy(filterableRisks, filterState).map((r) => r.id));
        return visibleRisks.filter((r) => passingIds.has(r.id));
    }, [visibleRisks, filterableRisks, filterState]);

    const contextValue = useMemo<IRiskDashboardContext>(() => {
        const rawModel = study?.evaluationModel as (EvaluationModel & { id?: string }) | undefined;

        return {
            study,
            risks,
            filteredRisks,
            filterableRisks,
            categories,
            screenTrackedChanges: trackedChanges,
            includeObsolete,
            filterVersion,
            includeDetectability: !!rawModel?.includeDetectability,
            evaluationModelId: rawModel?.id,
            evaluationModel: rawModel,
        };
    }, [study, risks, filteredRisks, filterableRisks, categories, trackedChanges, includeObsolete, filterVersion]);

    const showObsoleteToggle = (
        <Toggle
            data-testid="show-obsolete-toggle"
            checked={includeObsolete}
            onChange={(e) => setIncludeObsolete(e.value)}
        >
            {tRiskLog('includeObsolete')}
        </Toggle>
    );

    let body: React.ReactNode;
    if (loading) {
        body = <Loading />;
    } else if (!panels || panels.length === 0) {
        body = (
            <div className="k-pos-relative k-h-full">
                <NoRecordsMessage message={t('noPanelsMessage')} className="k-centered" />
            </div>
        );
    } else {
        body = (
            <div className={styles.panel_container}>
                {panels
                    .filter((p) => p !== null)
                    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                    .map((panel) => (
                        <React.Fragment key={panel.panelId}>{panelComponents[panel.panelId ?? 0]}</React.Fragment>
                    ))}
            </div>
        );
    }

    return (
        <RiskDashboardContext value={contextValue}>
            <FeActionsBar filterActions headerExtra={showObsoleteToggle} onTrackedChangesChange={setTrackedChanges} />
            {body}
        </RiskDashboardContext>
    );
};

const RiskDashboardWrapper = ({ study }: IRiskDashboardWrapperProps) => {
    const content = <Content study={study} />;
    return <>{content}</>;
};

const RiskDashboard = ({ study }: LoggedInPageProps) => <RiskDashboardWrapper study={study} />;

export default RiskDashboard;
